import { Injectable } from '@angular/core';

import { BehaviorSubject, Observable, Subject } from 'rxjs';
import {
  HttpClient,
  HttpHeaders,
  HttpParams,
  HttpResponse
} from '@angular/common/http';
import { Group } from '@iv/model/group';

@Injectable({providedIn: 'root'})
export class FavouritesService {
 private _presetDirection$: BehaviorSubject<string> = new BehaviorSubject<string>('desc');
 private _currentPreset$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
 private _showDirectionLoader: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false)
 private _showPresetLoader: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false)
 mode = 'DELAYED_STOCKS'

  get presetDirection(): Observable<string> {
    return this._presetDirection$.asObservable();
  }

  get currentPreset(): Observable<number> {
    return this._currentPreset$.asObservable();
  }

  get showDirectionLoader(): Observable<boolean> {
    return this._showDirectionLoader.asObservable();
  }

  constructor(private http: HttpClient) { }

  public setPresetDirection(direction: string): void {
    this._presetDirection$.next(direction)
  }

  public setCurrentPreset(preset: number): void {
    this._currentPreset$.next(preset)
  }

  public setShowDirectionLoader(showLoader:boolean) {
    this._showDirectionLoader.next(showLoader)
  }

  public getAll(): Observable<HttpResponse<Group[]>> {
    return this.http.get<Group[]>('/favorites/list', {observe: 'response'});
  }

  public getDefaultGroups(): Observable<HttpResponse<Group[]>> {
    return this.http.get<Group[]>('/favorites/listDefault', {observe: 'response'});
  }

  getGroupData(
    mode: string,
    groupId: number,
    columns?: any[],
    screenerType?: string,
    termType?: string,
    tradeDatePeriod?: number,
    period?: number,
    percent?: number | string,
    sendUserMetrics?: boolean
  ): Observable<HttpResponse<any>> {
    if (columns !== undefined && columns.length === 0) {
      columns = ['symbol', 'companyname', 'exchange', 'close', 'change', 'changepercent', 'optvol', 'hv20_eod', 'ivx30'];
    }

    const params = {
      rtMode: mode.split('_')[0],
      groupId: groupId,
      columns: columns
    };

    if (screenerType) {
      params['screenerType'] = screenerType;
    }

    if (termType) {
      params['termType'] = termType;
    }

    if (tradeDatePeriod) {
      params['tradeDatePeriod'] = tradeDatePeriod;
    }

    if (period) {
      params['period'] = period;
    }

    if (percent) {
      params['percent'] = percent;
    }

    if (sendUserMetrics) {
      params['sendUserMetrics'] = sendUserMetrics;
    }

    return this.http
      .get('/favorites/instruments', {
        observe: 'response', params: this.getHttpParams(params)
      });
  }

  addInstruments(groupId: number, stockIds: number[]): Observable<any> {
    return this.postToService('addInstrument', {groupId: groupId, stockId: stockIds});
  }

  deleteInstrument(groupId: number, stockIds: number[]): Observable<any> {
    return this.postToService('deleteInstrument', {groupId: groupId, stockIds: stockIds});
  }

  createGroup(groupName: string): Observable<any> {
    return this.postToService('create', {groupName: groupName});
  }

  deleteGroup(groupId: number): Observable<any> {
    return this.postToService('delete', {groupId: groupId});
  }

  renameGroup(groupId: number, groupName: string): Observable<any> {
    return this.postToService('rename', {groupId: groupId, groupName: groupName});
  }

  uploadDataBlob(
    groupId: number,
    mode,
    stockIds: number[],
    columns?: string[],
    name?: string,
    screenerType?: string,
    termType?: string,
    tradeDatePeriod?: number,
    period?: number,
    percent?: number
  ) {
    let params = '';
    params += '&groupId=' + String(groupId);
    params += '&mode=' + String(mode);
    const stocks = JSON.stringify(stockIds);
    columns.forEach(item => {
      params += '&columns=' + String(item);
    });
    if (name) {
      params += '&fileName=' + name.split(' ').join('_');
    }
    if (screenerType) {
      params += '&screenerType=' + String(screenerType);
      if (termType) params += '&termType=' + String(termType);
      if (tradeDatePeriod) params += '&tradeDatePeriod=' + String(tradeDatePeriod);
      if (period) params += '&period=' + String(period);
      if (percent) params += '&percent=' + String(percent);
    }
    const headers = new HttpHeaders({'Content-Type': 'application/json'});
    return this.http.post(`/favorites/instruments/csv?${params}`, stocks, {
      headers: headers,
      responseType: 'text'
    });
  }

  getHttpParams(paramsObj: Object) {
    let httpParams = new HttpParams();
    for (const key of Object.keys(paramsObj)) {
      httpParams = httpParams.set(key, paramsObj[key]);
    }
    return httpParams;
  }

  getColumns(): Observable<any> {
    // TODO может быть отфильтровать по mode...
    return this.http.get('/iv-live/assets/favourites_columns.json?version=5');
  }

  getIVXChangePreset(quantity: number, direction: string, groupId:number): Observable<any> {
    return this.http.get(`/favorites/stock-views/IVXchange?topCount=${quantity}&sortBy=${direction}&rtMode=${this.mode}&groupId=${groupId}`);
  }

  getVolumePreset(quantity: number, direction: string, groupId:number): Observable<any> {
    return this.http.get(`/favorites/stock-views/volume?topCount=${quantity}&sortBy=${direction}&rtMode=${this.mode}&groupId=${groupId}`);
  }

  getIVXvsHVPreset(quantity: number, direction: string, groupId:number): Observable<any> {
    return this.http.get(`/favorites/stock-views/IVXvsHV?topCount=${quantity}&sortBy=${direction}&rtMode=${this.mode}&groupId=${groupId}`);
  }

  getIVXRangePreset(quantity: number, direction: string, groupId:number): Observable<any> {
    return this.http.get(`/favorites/stock-views/IVXrange?topCount=${quantity}&sortBy=${direction}&rtMode=${this.mode}&groupId=${groupId}`);
  }

  getVolSkewPreset(quantity: number, direction: string, groupId:number): Observable<any> {
    return this.http.get(`/favorites/stock-views/volskew?topCount=${quantity}&sortBy=${direction}&rtMode=${this.mode}&columns=d10_eod,d25_eod&groupId=${groupId}`);
  }

  getCurrentDate(): Promise<any> {
    return this.http.get('/user-settings/currentDate', {responseType: 'text'})
      .toPromise()
      .then(response => {
        return response;
      })
      .catch(this.handleError);
  }

  uploadInstruments(file: File, groupId: number): Observable<any> {
    const formData = new FormData();
    formData.append("file", file);
    return  this.http.post(`/favorites/groups/${groupId}/upload-instruments`, formData);
  }

  private postToService(method: string, paramsObj: Object) {
    const body = this.getHttpParams(paramsObj);
    return this.http.post(`/favorites/${method}`, body);
  }

  private handleError(error: any): Promise<any> {
    console.error('An error occured', error);
    return Promise.reject(error.message || error);
  }

}
